import { formatShortcut, InputRichText, Tooltip } from '@dixa/lib-shared-habla';
import { conversationLanguages } from 'lib-shared-languages';
import { useCallback, useContext, useMemo, useRef } from 'react';
import { useUnmount } from 'react-use';

import { aiApi } from 'apis/ai';
import { copilotUtils } from 'utils/copilot';
import { useCopilotTrial } from 'utils/useCopilotTrial';
import { useEditorContext } from 'utils/useEditorContext';

import { PinnedActionProps } from '../copilotPinnedActions.types';

import { UIBlockerError } from './uiBlockerError';
import { UIBlockerLoading } from './uiBlockerLoading';

export const QuickTranslate = ({
  languageCode,
  text,
  csid,
}: PinnedActionProps): JSX.Element | null => {
  const [createTranslation, { isLoading }] =
    aiApi.useCreateAiTextTranslationMutation();
  const { content } = useContext(InputRichText.Context);
  const { replace } = useEditorContext();

  const { hasCopilot } = useCopilotTrial();
  const triggerRef = useRef<ReturnType<typeof createTranslation> | null>(null);

  const languageName = useMemo(() => {
    const language = conversationLanguages.find((lng) =>
      languageCode?.startsWith(lng.code),
    );
    return language?.name || '';
  }, [languageCode]);
  const hasLanguage = !!(languageCode && languageName);
  const { isDisabled, tooltipContent } = copilotUtils.quickTranslate.getStatus({
    text,
    hasLanguage,
    isLoading,
  });

  const shortcut = formatShortcut([[['$metaOrCtrl', 'Shift'], 'KeyT']]);
  const tooltipWithShortcut = shortcut
    ? `${tooltipContent} (${shortcut})`
    : tooltipContent;

  useUnmount(() => {
    if (triggerRef.current) {
      triggerRef.current.abort();
    }
  });

  const onClickHandler = useCallback(() => {
    content.setUiBlocker(
      <UIBlockerLoading loadingText={`Translating to ${languageName}`} />,
    );

    const trigger = createTranslation({
      language: languageName,
      text,
      csid,
      direction: 'outbound',
    });
    triggerRef.current = trigger;

    trigger
      .unwrap()
      .then((data) => {
        triggerRef.current = null;
        replace(data?.text ?? '');
        content.setUiBlocker(null);
      })
      .catch(() => {
        triggerRef.current = null;
        content.setUiBlocker(
          <UIBlockerError
            onClick={() => {
              content.setUiBlocker(null);
            }}
            errorText="Failed to translate"
          />,
        );
      });
  }, [content, createTranslation, languageName, replace, text]);

  if (!hasCopilot) {
    return null;
  }

  return (
    <Tooltip content={tooltipWithShortcut}>
      <InputRichText.ToolbarButton
        icon="translate-auto"
        onClick={onClickHandler}
        disabled={isDisabled}
      />
    </Tooltip>
  );
};
